import { useEffect, useState } from 'react';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { useDebounceCallback } from '@react-hook/debounce';
import { teamsSelector } from 'state/selectors/teams';
import { licenseSelector } from 'state/selectors/license';
import { getPageConfig } from './TeamsConfig.service';
import { getAllTeamsGroup } from 'common/utils/utils';
import { LB_COLOR_PICKER_PALETTE } from '@linearb/buzz';
import useFF from 'common/hooks/settings/useFF';
import { FEATURE_FLAGS_NAMES } from '@linearb/linjs-common';
import useTeamMembers from './useTeamMembers';

const useTeamsConfigProps = ({ type, team }) => {
  const { maxContributors: maxMembers } = useSelector(licenseSelector);
  const newUserManagementEnabled = useFF(
    FEATURE_FLAGS_NAMES.newUserManagementEnabled
  );
  const {
    parent_id: editTeamParentId,
    children: editTeamChildren = [],
    name: editTeamName,
    color: editTeamColor,
    initials: editTeamInitials,
    isSynced: editTeamIsSynced = false
  } = team;

  const [errors, setErrors] = useState({});
  const randomColor =
    LB_COLOR_PICKER_PALETTE?.[
      Math.floor(Math.random() * LB_COLOR_PICKER_PALETTE.length)
    ];
  const [teamProps, setTeamProps] = useState({
    name: editTeamName || '',
    color: editTeamColor || randomColor,
    initials: editTeamInitials || '',
    isSynced: editTeamIsSynced
  });
  const teams = useSelector(teamsSelector) || [];
  const allTeamsGroup = getAllTeamsGroup(teams);
  const isAllTeamsGroup = !_.isEmpty(allTeamsGroup);

  // TO DO: when new all teams group is GA - no need for the condition here.
  const defaultTeamParentGroup = isAllTeamsGroup ? allTeamsGroup : [];
  const teamParentGroup =
    teams.find(({ id }) => id === editTeamParentId) || defaultTeamParentGroup;
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);

  // members are conditionally Contributors or Users according to FF: newUserManagementEnabled
  const { members, setMembers } = useTeamMembers({ team });

  const [parentGroup, setParentGroup] = useState(
    [teamParentGroup].filter(Boolean)
  );
  const [nestedTeams, setNestedTeams] = useState(editTeamChildren);
  const [isWithHistory, setIsWithHistory] = useState(true);
  const [withoutBackfill, setWithoutBackfill] = useState(false);

  const { nameError, showNestedTeams, mandatoryDirectMembers } = getPageConfig({
    type,
    team
  });

  const { name, initials } = teamProps;
  const teamsNames = teams.map(({ name }) => name);

  const onNameChange = useDebounceCallback(() => {
    if (teamsNames.includes(name) && name !== editTeamName) {
      setErrors({
        ...errors,
        name: nameError
      });
    } else {
      const { name, ...errorsRest } = errors;
      setErrors({
        ...errorsRest
      });
    }

    if (name && initials?.length < 2) {
      const splittedName = name.split(' ');
      const suggetedInitials = splittedName
        .slice(0, 2)
        .map((word = '') => word.charAt(0).toUpperCase())
        .join('');
      setTeamProps({
        ...teamProps,
        initials: suggetedInitials
      });
    }
  }, 700);

  const checkIsSavedEnabled = () => {
    if (newUserManagementEnabled) {
      return false;
    }

    const { name, color, initials } = teamProps;

    return (
      Object.keys(errors).length === 0 &&
      !!name &&
      !!color &&
      !!initials &&
      parentGroup.length > 0 &&
      (mandatoryDirectMembers ? members.length > 0 : true) &&
      (showNestedTeams ? nestedTeams.length > 0 : true)
    );
  };

  useEffect(() => {
    onNameChange();
  }, [name]);

  useEffect(() => {
    setIsSaveEnabled(checkIsSavedEnabled());
  }, [errors, teamProps, parentGroup, nestedTeams, members]);

  return {
    errors,
    teamProps,
    setTeamProps,
    isSaveEnabled,
    setIsSaveEnabled,
    members,
    setMembers,
    parentGroup,
    setParentGroup,
    nestedTeams,
    setNestedTeams,
    isWithHistory,
    setIsWithHistory,
    withoutBackfill,
    setWithoutBackfill,
    maxMembers,
    newUserManagementEnabled
  };
};

export default useTeamsConfigProps;
